import type { AuthCredentials, AuthResponse } from 'frappe-js-sdk/lib/auth/types'
import type { FrappeDoc, GetDocListArgs } from 'frappe-js-sdk/lib/db/types'
import type { Error as FrappeAppError, TokenParams } from 'frappe-js-sdk/lib/frappe_app/types'
// import type { PiniaPlugin } from 'pinia'
import type { InjectionKey, App as VueApp, Plugin as VuePlugin } from 'vue'

import {
  FrappeApp,
  type FrappeAuth,
  type FrappeCall,
  type FrappeDB,
  type FrappeFileUpload,
} from 'frappe-js-sdk'
// import { markRaw, ref, toRef } from 'vue'

// import { handleUnknownError } from '@/utilities/errors'
import { installFrappeCsrfTokenHandling } from '@/utilities/frappeCsrfToken'

type FrappeAppConstructorParameterCustomHeaders = FrappeApp['customHeaders']
type FrappeAppConstructorParameterName = FrappeApp['name']
type FrappeAppConstructorParameterUrl = FrappeApp['url']

type FrappeAppConstructorParameterTokenParams = TokenParams

// derived from app token function
type FrappeAppTokenReturnValue = string | undefined
// derived from auth token function
type FrappeAuthTokenReturnValue = string | undefined
// derived from call token function
type FrappeCallTokenReturnValue = string | undefined
// derived from data token function
type FrappeDataTokenReturnValue = string | undefined
// derived from file token function
type FrappeFileTokenReturnValue = string | undefined

type FrappeAuthRequest = AuthCredentials
type FrappeAuthResponse = AuthResponse
// derived from auth getLoggedInUser function
type FrappeAuthUsernameResponse = string
// derived from auth forgetPassword function
type FrappeAuthForgetPasswordUsernameRequest = string

// derived from call delete function
type FrappeCallRequestDeleteParams = unknown
// derived from call delete function
type FrappeCallRequestDeletePath = string
// derived from call get function
type FrappeCallRequestGetParams = Record<string, unknown>
// derived from call get function
type FrappeCallRequestGetPath = string
// derived from call post function
type FrappeCallRequestPostParams = unknown
// derived from call post function
type FrappeCallRequestPostPath = string
// derived from call put function
type FrappeCallRequestPutParams = unknown
// derived from call put function
type FrappeCallRequestPutPath = string

// derived from db getDocList function
type FrappeDataRequestDocListDocTypeName = string
type FrappeDataRequestDoc<AdditionalDocTypeFields> = FrappeDoc<AdditionalDocTypeFields>
type FrappeDataRequestDocListArgumentList<DocTypeType> = GetDocListArgs<DocTypeType>
type FrappeDataResponseDocList<DocTypeType> = DocTypeType[]
type FrappeDataResponseDoc<DocTypeType> = DocTypeType

type FrappeInstance = FrappeApp

interface FrappeAppInstance
  extends Omit<FrappeInstance, 'auth' | 'call' | 'db' | 'file' | 'token' | 'tokenType'> {
  token: FrappeInstance['token'] | undefined
  tokenType: FrappeInstance['tokenType'] | undefined
}
type FrappeAuthInstance = FrappeAuth
type FrappeCallInstance = FrappeCall
type FrappeDataInstance = FrappeDB
type FrappeFileInstance = FrappeFileUpload

interface FrappeSubInstances {
  frappeApp: FrappeAppInstance
  frappeAuth: FrappeAuthInstance
  frappeCall: FrappeCallInstance
  frappeData: FrappeDataInstance
  frappeFile: FrappeFileInstance
}

// derived from "FrappeApp" constructor type definition
interface FrappeAppConstructorOptions {
  url: FrappeAppConstructorParameterUrl
  customHeaders?: FrappeAppConstructorParameterCustomHeaders
  name?: FrappeAppConstructorParameterName
  tokenParams?: FrappeAppConstructorParameterTokenParams
}

interface FrappePluginOptions extends Omit<FrappeAppConstructorOptions, 'url'> {
  frappeInstanceUrl?: FrappeAppConstructorParameterUrl | undefined
}

interface FrappeAppErrorAdditionalAttributes {
  // indicator for expired session (frappe framework not use 401 error code)
  session_expired?: number | undefined
}

type FrappeInstanceError = FrappeAppError & FrappeAppErrorAdditionalAttributes

// type the aditional frappe app instances in pinia stores (for pinia plugin variant)
// declare module 'pinia' {
//   export interface PiniaCustomProperties {
//     frappeApp: FrappeAppInstance
//     frappeAuth: FrappeAuthInstance
//     frappeCall: FrappeCallInstance
//     frappeData: FrappeDataInstance
//     frappeFile: FrappeFileInstance
//   }
// }

// define typeguard for error type here, derived from "FrappeApp" error type (based on type definition)
// (source code definition creates an object with axios attributes, needs "axios" package to be handled here,
// no pass-through types from the framework)
const isFrappeInstanceError = (
  isPossibleFrappeInstanceError: unknown,
): isPossibleFrappeInstanceError is FrappeInstanceError => {
  let isExistAllRequireFrappeInstanceErrorAttributes = false

  if (isPossibleFrappeInstanceError instanceof Object) {
    // check require attributes of the FrappeInstanceError interface
    // adjust when frappe typescript framework changes
    const isExistRequireAttributeException = Object.hasOwn(
      isPossibleFrappeInstanceError,
      'exception',
    )
    const isExistRequireAttributeHttpStatus = Object.hasOwn(
      isPossibleFrappeInstanceError,
      'httpStatus',
    )
    const isExistRequireAttributeHttpStatusText = Object.hasOwn(
      isPossibleFrappeInstanceError,
      'httpStatusText',
    )
    const isExistRequireAttributeMessage = Object.hasOwn(isPossibleFrappeInstanceError, 'message')

    isExistAllRequireFrappeInstanceErrorAttributes =
      isExistRequireAttributeException &&
      isExistRequireAttributeHttpStatus &&
      isExistRequireAttributeHttpStatusText &&
      isExistRequireAttributeMessage
  }

  return isExistAllRequireFrappeInstanceErrorAttributes
}

//-----------------------------------------------------------------------------

const frappeInstanceInitialize = (frappeInstanceOptions: FrappePluginOptions): FrappeInstance => {
  // Create a frappe instance with neccessary parameters
  const frappeConstructorOptions: FrappeAppConstructorOptions = {
    customHeaders: frappeInstanceOptions.customHeaders,
    url: frappeInstanceOptions.frappeInstanceUrl ?? '',
  }

  if (frappeInstanceOptions.name) {
    frappeConstructorOptions.name = frappeInstanceOptions.name
  }
  if (frappeInstanceOptions.tokenParams) {
    frappeConstructorOptions.tokenParams = frappeInstanceOptions.tokenParams
  }

  const frappeInstance: FrappeInstance = new FrappeApp(
    frappeConstructorOptions.url,
    frappeConstructorOptions.tokenParams,
    frappeConstructorOptions.name,
  )

  return frappeInstance
}

// ----- Begin: use for frappe framework communication inspection -----

// use this to print error information only (framework needs unchanged error object for internal error handling)
// const handleCommunicationError = async (communicationError: unknown): Promise<void> => {
//   if (communicationError instanceof Error) {
//     const communicationErrorPreText = 'Handle Axios Error:'

//     console.error(communicationErrorPreText, `\n${communicationError.name}\n${communicationError.message}`)
//     // debug additional error attributes not defined in the type
//     // console.debug('full frappe framework axios error object', communicationError)

//     // rethrow error object
//     await Promise.reject(communicationError)
//   } else {
//     handleUnknownError(communicationError)
//   }
// }

// const frappeAxiosInstanceInitialize = (frappeInstance: FrappeInstance): void => {
//   const frappeAxiosInstance = frappeInstance.axios

//   // set axios instance default headers for all requests
//   // frappeAxiosInstance.defaults.headers.common['headerKey'] = 'headerValue'

//   // request interceptors
//   frappeAxiosInstance.interceptors.request.use((requestConfig) => {
//     // define request config adjustments here
//     return requestConfig
//   }, handleCommunicationError)

//   // response interceptors to handle error and success messages
//   frappeAxiosInstance.interceptors.response.use((response) => {
//     // Success handling
//     return response
//   }, handleCommunicationError)
// }

// ----- End: use for frappe framework communication inspection -----

const frappeAppInstanceKey: InjectionKey<FrappeAppInstance> = Symbol('Frappe: Application Instance')
const frappeAuthInstanceKey: InjectionKey<FrappeAuthInstance> = Symbol(
  'Frappe: Authorization Instance',
)
const frappeCallInstanceKey: InjectionKey<FrappeCallInstance> = Symbol('Frappe: API Call Instance')
const frappeDataInstanceKey: InjectionKey<FrappeDataInstance> = Symbol('Frappe: Data Base Instance')
const frappeFileInstanceKey: InjectionKey<FrappeFileInstance> = Symbol(
  'Frappe: File Upload Instance',
)

const frappeSubInstancesInitialize = (frappeInstance: FrappeInstance): FrappeSubInstances => {
  const frappeAppInstance: FrappeAppInstance = {
    axios: frappeInstance.axios,
    name: frappeInstance.name,
    url: frappeInstance.url,
    useToken: frappeInstance.useToken,

    token: frappeInstance.token,
    tokenType: frappeInstance.tokenType,
  }

  // initialize frappe authorization instance
  const frappeAuthInstance = frappeInstance.auth()
  // initialize frappe api call instance
  const frappeCallInstance = frappeInstance.call()
  // initialize frappe database instance
  const frappeDataInstance = frappeInstance.db()
  // initialize frappe file upload instance
  const frappeFileInstance = frappeInstance.file()

  return {
    frappeApp: frappeAppInstance,
    frappeAuth: frappeAuthInstance,
    frappeCall: frappeCallInstance,
    frappeData: frappeDataInstance,
    frappeFile: frappeFileInstance,
  }
}

const integrateFrappeInstance = (vueApp: VueApp, frappeInstance: FrappeInstance): void => {
  // initialize frappe app instance
  const frappeSubInstances: FrappeSubInstances = frappeSubInstancesInitialize(frappeInstance)

  // app level provide for frappe instances (composition api)
  vueApp.provide(frappeAppInstanceKey, frappeSubInstances.frappeApp)
  vueApp.provide(frappeAuthInstanceKey, frappeSubInstances.frappeAuth)
  vueApp.provide(frappeCallInstanceKey, frappeSubInstances.frappeCall)
  vueApp.provide(frappeDataInstanceKey, frappeSubInstances.frappeData)
  vueApp.provide(frappeFileInstanceKey, frappeSubInstances.frappeFile)

  // inject counterpart anywhere in the application:
  // instanceValue = inject(instanceKey) // type: KeyType | undefined
  // const testFrappeAppInstance = inject(frappeAppInstanceKey)
}

const frappeVuePlugin: VuePlugin<FrappePluginOptions> = {
  install: (app: VueApp, options): void => {
    // initialize frappe application instance
    const frappeInstance = frappeInstanceInitialize(options)

    // send the csrf token of the current session with every state changing request
    installFrappeCsrfTokenHandling(frappeInstance.axios)

    // ---- use for frappe framework communication inspection ---
    // initialize frappe axios instance
    // frappeAxiosInstanceInitialize(frappeInstance)

    integrateFrappeInstance(app, frappeInstance)
  },
}

// pinia plugin variant

// const frappePiniaPlugin: PiniaPlugin = (context) => {
//   const { store: currentStore } = context

//   const frappeInstance = frappeInstanceInitialize()

//   frappeAxiosInstanceInitialize(frappeInstance)

//   const frappeSubInstances: FrappeSubInstances = frappeSubInstancesInitialize(frappeInstance)

//   // raw type, not reactive external library
//   currentStore.frappeApp = markRaw(frappeSubInstances.frappeApp)
//   currentStore.frappeAuth = markRaw(frappeSubInstances.frappeAuth)
//   currentStore.frappeCall = markRaw(frappeSubInstances.frappeCall)
//   currentStore.frappeData = markRaw(frappeSubInstances.frappeData)
//   currentStore.frappeFile = markRaw(frappeSubInstances.frappeFile)

//   if (isEnvironmentDevelopment) {
//     // add any keys you set on the store
//     // eslint-disable-next-line no-underscore-dangle
//     currentStore._customProperties.add('frappeApp')
//     // eslint-disable-next-line no-underscore-dangle
//     currentStore._customProperties.add('frappeAuth')
//     // eslint-disable-next-line no-underscore-dangle
//     currentStore._customProperties.add('frappeCall')
//     // eslint-disable-next-line no-underscore-dangle
//     currentStore._customProperties.add('frappeData')
//     // eslint-disable-next-line no-underscore-dangle
//     currentStore._customProperties.add('frappeFile')
//   }
// }

export {
  type FrappeAppInstance,
  frappeAppInstanceKey,
  type FrappeAppTokenReturnValue,
  type FrappeAuthForgetPasswordUsernameRequest,
  type FrappeAuthInstance,
  frappeAuthInstanceKey,
  type FrappeAuthRequest,
  type FrappeAuthResponse,
  type FrappeAuthTokenReturnValue,
  type FrappeAuthUsernameResponse,
  type FrappeCallInstance,
  frappeCallInstanceKey,
  type FrappeCallRequestDeleteParams,
  type FrappeCallRequestDeletePath,
  type FrappeCallRequestGetParams,
  type FrappeCallRequestGetPath,
  type FrappeCallRequestPostParams,
  type FrappeCallRequestPostPath,
  type FrappeCallRequestPutParams,
  type FrappeCallRequestPutPath,
  type FrappeCallTokenReturnValue,
  type FrappeDataInstance,
  frappeDataInstanceKey,
  type FrappeDataRequestDoc,
  type FrappeDataRequestDocListArgumentList,
  type FrappeDataRequestDocListDocTypeName,
  type FrappeDataResponseDoc,
  type FrappeDataResponseDocList,
  type FrappeDataTokenReturnValue,
  type FrappeFileInstance,
  frappeFileInstanceKey,
  type FrappeFileTokenReturnValue,
  type FrappeInstanceError,
  type FrappePluginOptions,
  // frappePiniaPlugin,
  frappeVuePlugin,
  isFrappeInstanceError,
}
